import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  Button, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import { 
  Edit as EditIcon, 
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Add as AddIcon,
  Search as SearchIcon,
  AutoAwesome as AutoAwesomeIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import promotionService from '../../services/marketingService';
import { Promotion, PromotionType } from '../../types/marketingTypes';
import axios from '../../utils/axiosConfig';
import { usePermissions } from '../../hooks/usePermissions';

// واجهة للكوبونات المرسلة عبر SMS
interface SentCoupon {
  _id: string;
  code: string;
  phoneNumber: string;
  message: string;
  sentAt: string;
  status: string;
  discountValue?: number;
  discountType?: string;
  maxUsageCount?: number;
  usageCount?: number;
  description?: string;
}

// أنواع الكوبونات التلقائية
type AutoCouponType = 'automatic' | 'automatic_sms' | 'automatic_whatsapp';

// دالة لتحديد نوع الكوبون من الوصف
const getCouponType = (description?: string): AutoCouponType => {
  if (!description) return 'automatic';
  
  const desc = description.toLowerCase();
  // كوبون SMS - يحتوي على "كوبون للرسالة النصية"
  if (desc.includes('كوبون للرسالة النصية') || desc.includes('sms')) {
    return 'automatic_sms';
  }
  // كوبون واتساب - يحتوي على "كوبون فريد للرسالة الجماعية" أو "واتس"
  if (desc.includes('كوبون فريد للرسالة الجماعية') || desc.includes('واتس') || desc.includes('الواتساب') || desc.includes('whatsapp')) {
    return 'automatic_whatsapp';
  }
  // كوبون تلقائي عادي - يحتوي على "كوبون تلقائي من"
  if (desc.includes('كوبون تلقائي من') || desc.includes('automatic')) {
    return 'automatic';
  }
  return 'automatic';
};

// دالة لعرض اسم نوع الكوبون بالعربية
const getCouponTypeLabel = (type: AutoCouponType): string => {
  switch (type) {
    case 'automatic':
      return 'كوبون تلقائي';
    case 'automatic_sms':
      return 'كوبون تلقائي SMS';
    case 'automatic_whatsapp':
      return 'كوبون تلقائي واتس';
    default:
      return 'كوبون تلقائي';
  }
};

const CouponList: React.FC = () => {
  const [coupons, setCoupons] = useState<Promotion[]>([]);
   const [sentCoupons, setSentCoupons] = useState<SentCoupon[]>([]);
   const [loading, setLoading] = useState<boolean>(true);
   const [sentCouponsLoading, setSentCouponsLoading] = useState<boolean>(false);
   const [error, setError] = useState<string>('');
   const [sentCouponsError, setSentCouponsError] = useState<string>('');
   const [page, setPage] = useState<number>(0);
   const [sentCouponsPage, setSentCouponsPage] = useState<number>(0);
    const [rowsPerPage, setRowsPerPage] = useState<number>(10);
    const [sentCouponsRowsPerPage, setSentCouponsRowsPerPage] = useState<number>(10);
    const [totalSentCoupons, setTotalSentCoupons] = useState<number>(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [couponToDelete, setCouponToDelete] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [sentCouponsSearchTerm, setSentCouponsSearchTerm] = useState<string>('');
  const [tabValue, setTabValue] = useState<number>(0);
  const [couponUsageMap, setCouponUsageMap] = useState<Map<string, number>>(new Map());
  const navigate = useNavigate();

  // صلاحيات الكوبونات التلقائية
  const { hasPermission } = usePermissions();
  const canViewAutoCoupons = hasPermission('marketing', 'auto-coupons', 'view_page');
  const canGenerateAutoCoupons = hasPermission('marketing', 'auto-coupons', 'generate_auto_coupons');
  const canDeleteCoupon = hasPermission('marketing', 'auto-coupons', 'delete_coupon');

  // جلب قائمة الكوبونات
  useEffect(() => {
    const fetchCoupons = async () => {
      try {
        setLoading(true);
        setError('');
        
        // استخدام خدمة جلب الحملات ثم تصفية الكوبونات فقط
        const response = await promotionService.getAllPromotions();
        const couponsList = response.filter(
          (promo: Promotion) => promo.promotionType === PromotionType.COUPON
        );
        setCoupons(couponsList);
      } catch (err) {
        console.error('Error fetching coupons:', err);
        setError('حدث خطأ أثناء جلب الكوبونات');
      } finally {
        setLoading(false);
      }
    };
    
    fetchCoupons();
  }, []);

    // حالات الكوبونات المرسلة
   const [sentCouponToDelete, setSentCouponToDelete] = useState<string | null>(null);
   const [deleteSentCouponDialogOpen, setDeleteSentCouponDialogOpen] = useState<boolean>(false);
   const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState<boolean>(false);
   
   // حالات الفلترة للكوبونات المرسلة
   const [couponTypeFilter, setCouponTypeFilter] = useState<string>('all');
   const [startDateFilter, setStartDateFilter] = useState<string>('');
   const [endDateFilter, setEndDateFilter] = useState<string>('');
   
   // حالات التحديد المتعدد
   const [selectedCoupons, setSelectedCoupons] = useState<string[]>([]);
   const [selectAll, setSelectAll] = useState<boolean>(false);
   
   // حالات إصدار الكوبونات التلقائية
   const [autoCouponDialogOpen, setAutoCouponDialogOpen] = useState<boolean>(false);
   const [selectedBaseCoupon, setSelectedBaseCoupon] = useState<string>('');
   const [autoCouponCount, setAutoCouponCount] = useState<number>(10);
  const [availableCouponsForGeneration, setAvailableCouponsForGeneration] = useState<Promotion[]>([]);
  const [loadingCoupons, setLoadingCoupons] = useState<boolean>(false);
  const [generatingCoupons, setGeneratingCoupons] = useState<boolean>(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  });


   // جلب الكوبونات المرسلة عبر SMS
    const fetchSentCoupons = async () => {
      if (tabValue !== 1) return; // فقط عندما يكون التبويب الثاني مفعلاً
      
      try {
        setSentCouponsLoading(true);
        setSentCouponsError('');
        
        // جلب الكوبونات مع Pagination والفلاتر
        const response = await axios.get('/marketing/sms/sent-coupons', {
          params: {
            limit: sentCouponsRowsPerPage,
            page: sentCouponsPage + 1, // API يستخدم page بدءاً من 1
            search: sentCouponsSearchTerm || undefined,
            type: couponTypeFilter !== 'all' ? couponTypeFilter : undefined,
            startDate: startDateFilter || undefined,
            endDate: endDateFilter || undefined
          }
        });
        setSentCoupons(response.data.data.sentCoupons || []);
        setTotalSentCoupons(response.data.total || 0);
      } catch (err) {
        console.error('Error fetching sent coupons:', err);
        setSentCouponsError('حدث خطأ أثناء جلب الكوبونات المرسلة');
      } finally {
        setSentCouponsLoading(false);
      }
    };

   useEffect(() => {
      fetchSentCoupons();
    }, [tabValue, sentCouponsPage, sentCouponsRowsPerPage, sentCouponsSearchTerm, couponTypeFilter, startDateFilter, endDateFilter]);

    // جلب بيانات استخدام الكوبونات من جدول الكوبونات
  useEffect(() => {
    const fetchCouponUsage = async () => {
      try {
        const response = await axios.get('/coupons');
        const coupons = response.data.data.coupons;
        const usageMap = new Map<string, number>(coupons.map((c: any) => [c.code, c.usageCount] as [string, number]));
        setCouponUsageMap(usageMap);
      } catch (err) {
        console.error('Error fetching coupon usage:', err);
      }
    };
    fetchCouponUsage();
  }, []);

   // جلب الكوبونات المتاحة للإصدار التلقائي
   const loadAvailableCouponsForGeneration = async () => {
     setLoadingCoupons(true);
     try {
       const response = await axios.get('/coupons');
       if (response.data.success) {
         setAvailableCouponsForGeneration(response.data.data.coupons || []);
       }
     } catch (err) {
       console.error('خطأ في تحميل الكوبونات:', err);
       setSnackbar({
         open: true,
         message: 'حدث خطأ أثناء تحميل الكوبونات',
         severity: 'error'
       });
     } finally {
       setLoadingCoupons(false);
     }
   };

   // إعادة تعيين التحديد عند تغيير البيانات
    useEffect(() => {
      setSelectedCoupons([]);
      setSelectAll(false);
    }, [sentCoupons]);


  // تصفية الكوبونات حسب البحث
  const filteredCoupons = coupons.filter(coupon => 
    coupon.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
    coupon.code?.toLowerCase().includes(searchTerm.toLowerCase())
  );

   // الكوبونات المرسلة (مُفلترة من قبل الخادم)
    const filteredSentCoupons = sentCoupons;

  // التعامل مع تغيير الصفحة
  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  // التعامل مع تغيير عدد الصفوف في الصفحة
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // التعامل مع تغيير الصفحة للكوبونات المرسلة
  const handleSentCouponsChangePage = (_: unknown, newPage: number) => {
    setSentCouponsPage(newPage);
    // الـ useEffect سيتولى جلب البيانات تلقائياً
  };

  // التعامل مع تغيير عدد الصفوف في الصفحة للكوبونات المرسلة
  const handleSentCouponsChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSentCouponsRowsPerPage(parseInt(event.target.value, 10));
    setSentCouponsPage(0);
    // الـ useEffect سيتولى جلب البيانات تلقائياً
  };

  // التعامل مع تغيير التبويب
  const handleTabChange = (_: unknown, newValue: number) => {
    // عند التبديل إلى التبويب الثاني (الكوبونات التلقائية)، التحقق من الصلاحية
    if (newValue === 1 && !canViewAutoCoupons) {
      return;
    }
    setTabValue(newValue);
  };

  // فتح حوار الحذف
  const handleOpenDeleteDialog = (id: string) => {
    setCouponToDelete(id);
    setDeleteDialogOpen(true);
  };

  // إغلاق حوار الحذف
  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setCouponToDelete(null);
  };

  // حذف كوبون
  const handleDeleteCoupon = async () => {
    if (!couponToDelete) return;

    if (!canDeleteCoupon) {
      setSnackbar({
        open: true,
        message: 'ليس لديك صلاحية حذف الكوبونات',
        severity: 'error'
      });
      handleCloseDeleteDialog();
      return;
    }

    try {
      await promotionService.deletePromotion(couponToDelete);
      setCoupons(prevCoupons => prevCoupons.filter(coupon => coupon._id !== couponToDelete));
      handleCloseDeleteDialog();
    } catch (err) {
      console.error('Error deleting coupon:', err);
      // يمكن إضافة إشعار خطأ هنا
    }
  };

    // التوجه إلى صفحة إنشاء كوبون جديد
  const handleAddCoupon = () => {
    navigate('/marketing/campaigns/new', { state: { defaultType: PromotionType.COUPON } });
  };

  // فتح نافذة إصدار الكوبونات التلقائية
  const handleOpenAutoCouponDialog = () => {
    if (!canGenerateAutoCoupons) {
      setSnackbar({
        open: true,
        message: 'ليس لديك صلاحية إصدار الكوبونات التلقائية',
        severity: 'error'
      });
      return;
    }
    setAutoCouponDialogOpen(true);
    setSelectedBaseCoupon('');
    setAutoCouponCount(10);
    loadAvailableCouponsForGeneration();
  };

  // إغلاق نافذة إصدار الكوبونات التلقائية
  const handleCloseAutoCouponDialog = () => {
    setAutoCouponDialogOpen(false);
  };

  // إصدار الكوبونات التلقائية
  const handleGenerateAutoCoupons = async () => {
    if (!selectedBaseCoupon) {
      setSnackbar({
        open: true,
        message: 'يجب اختيار كوبون أصلي',
        severity: 'error'
      });
      return;
    }

    if (autoCouponCount <= 0) {
      setSnackbar({
        open: true,
        message: 'يجب إدخال عدد صحيح موجب للكوبونات',
        severity: 'error'
      });
      return;
    }

    setGeneratingCoupons(true);
    try {
      // ملاحظة: تأكد من صحة endpoint في الخادم
      const response = await axios.post('/marketing/promotions/generate-coupon-codes', {
        baseCouponId: selectedBaseCoupon,
        count: autoCouponCount
      });

      if (response.data.success) {
        setSnackbar({
          open: true,
          message: `تم إصدار ${autoCouponCount} كوبون تلقائي بنجاح`,
          severity: 'success'
        });
        handleCloseAutoCouponDialog();
        // إعادة تحميل الكوبونات المرسلة إذا كان التبويب مفعل
        if (tabValue === 1) {
          fetchSentCoupons();
        }
      } else {
        setSnackbar({
          open: true,
          message: response.data.message || 'فشل إصدار الكوبونات',
          severity: 'error'
        });
      }
    } catch (err: any) {
      console.error('Error generating auto coupons:', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'حدث خطأ أثناء إصدار الكوبونات',
        severity: 'error'
      });
    } finally {
      setGeneratingCoupons(false);
    }
  };

  // التوجه إلى صفحة تعديل كوبون

  const handleEditCoupon = (id: string) => {
    navigate(`/marketing/campaigns/${id}/edit`);
  };

  // التوجه إلى صفحة عرض تفاصيل الكوبون
  const handleViewCoupon = (id: string) => {
    navigate(`/marketing/campaigns/${id}`);
  };

  // فتح حوار حذف الكوبون المرسل
  const handleOpenDeleteSentCouponDialog = (id: string) => {
    setSentCouponToDelete(id);
    setDeleteSentCouponDialogOpen(true);
  };

  // إغلاق حوار حذف الكوبون المرسل
  const handleCloseDeleteSentCouponDialog = () => {
    setDeleteSentCouponDialogOpen(false);
    setSentCouponToDelete(null);
  };

   // حذف الكوبون المرسل
   const handleDeleteSentCoupon = async () => {
     if (!sentCouponToDelete) return;

     if (!canDeleteCoupon) {
       setSnackbar({
         open: true,
         message: 'ليس لديك صلاحية حذف الكوبونات',
         severity: 'error'
       });
       handleCloseDeleteSentCouponDialog();
       return;
     }

     try {
       await axios.delete(`/marketing/sms/sent-coupons/${sentCouponToDelete}`);
       setSentCoupons(prevCoupons => prevCoupons.filter(coupon => coupon._id !== sentCouponToDelete));
       handleCloseDeleteSentCouponDialog();
     } catch (err) {
       console.error('Error deleting sent coupon:', err);
     }
   };

   // تحديد/إلغاء تحديد جميع الكوبونات
   const handleSelectAll = (checked: boolean) => {
     if (checked) {
       // تحديد جميع الكوبونات في البيانات المصفاة
       const allIds = filteredSentCoupons.map(coupon => coupon._id);
       setSelectedCoupons(allIds);
     } else {
       // إلغاء تحديد جميع الكوبونات
       setSelectedCoupons([]);
     }
   };

   // تحديد/إلغاء تحديد كوبون واحد
   const handleSelectCoupon = (couponId: string, checked: boolean) => {
     if (checked) {
       setSelectedCoupons(prev => [...prev, couponId]);
     } else {
       setSelectedCoupons(prev => prev.filter(id => id !== couponId));
     }
   };

   // تحديث حالة selectAll بناءً على الكوبونات المحددة
   useEffect(() => {
     if (filteredSentCoupons.length === 0) {
       setSelectAll(false);
     } else {
       setSelectAll(selectedCoupons.length === filteredSentCoupons.length);
     }
   }, [selectedCoupons, filteredSentCoupons.length]);

   // الحذف الجماعي للكوبونات المحددة
   const handleBulkDeleteCoupons = async () => {
     if (selectedCoupons.length === 0) return;

     if (!canDeleteCoupon) {
       setSnackbar({
         open: true,
         message: 'ليس لديك صلاحية حذف الكوبونات',
         severity: 'error'
       });
       setBulkDeleteDialogOpen(false);
       return;
     }

     try {
       // حذف كل كوبون على حدة
       const deletePromises = selectedCoupons.map(id => 
         axios.delete(`/marketing/sms/sent-coupons/${id}`)
       );
       
       await Promise.all(deletePromises);
       
       setSentCoupons(prevCoupons => 
         prevCoupons.filter(coupon => !selectedCoupons.includes(coupon._id))
       );
       
       setSelectedCoupons([]);
       setSelectAll(false);
       setBulkDeleteDialogOpen(false);
       
       setSnackbar({
         open: true,
         message: `تم حذف ${selectedCoupons.length} كوبون بنجاح`,
         severity: 'success'
       });
     } catch (err) {
       console.error('Error bulk deleting coupons:', err);
       setSnackbar({
         open: true,
         message: 'حدث خطأ أثناء حذف الكوبونات',
         severity: 'error'
       });
     }
   };

   // إغلاق حوار الحذف الجماعي
   const handleCloseBulkDeleteDialog = () => {
     setBulkDeleteDialogOpen(false);
   };

  // عرض حالة التحميل
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  // عرض رسالة الخطأ
  if (error) {
    return (
      <Box sx={{ py: 2 }}>
        <Typography color="error" align="center">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* التبويبات */}
      <Tabs
        value={tabValue}
        onChange={handleTabChange}
        aria-label="coupon tabs"
        sx={{ mb: 2 }}
      >
        <Tab label="الكوبونات" />
        <Tab label="الكوبونات التلقائية" />
      </Tabs>

      {/* محتوى التبويب الأول - الكوبونات العادية */}
      {tabValue === 0 && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" component="h2" dir="rtl">
              الكوبونات
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleAddCoupon}
            >
              إنشاء كوبون جديد
            </Button>
          </Box>

          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              placeholder="ابحث عن كوبون..."
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ maxWidth: 500 }}
            />
          </Box>

          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }} aria-label="جدول الكوبونات">
              <TableHead>
                <TableRow>
                  <TableCell align="right">اسم الكوبون</TableCell>
                  <TableCell align="right">الرمز</TableCell>
                  <TableCell align="right">تاريخ البداية</TableCell>
                  <TableCell align="right">تاريخ النهاية</TableCell>
                  <TableCell align="right">الحد الأقصى للاستخدام</TableCell>
                  <TableCell align="right">عدد مرات الاستخدام</TableCell>
                  <TableCell align="right">الحالة</TableCell>
                  <TableCell align="center">الإجراءات</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredCoupons.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      {searchTerm ? 'لا توجد كوبونات تطابق البحث' : 'لا توجد كوبونات متاحة'}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredCoupons
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((coupon) => (
                      <TableRow key={coupon._id} hover>
                        <TableCell align="right">{coupon.name}</TableCell>
                        <TableCell align="right">
                          <Chip
                            label={coupon.code}
                            color="secondary"
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right">
                          {new Date(coupon.startDate).toLocaleDateString('ar-SA')}
                        </TableCell>
                        <TableCell align="right">
                          {new Date(coupon.endDate).toLocaleDateString('ar-SA')}
                        </TableCell>
                        <TableCell align="right">
                          {coupon.maxUses ? coupon.maxUses : 'غير محدود'}
                        </TableCell>
                        <TableCell align="right">
                          {coupon.code ? (couponUsageMap.get(coupon.code) || 0) : 0}
                        </TableCell>
                        <TableCell align="right">
                          <Chip
                            label={coupon.isActive ? 'نشط' : 'غير نشط'}
                            color={coupon.isActive ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <IconButton 
                            aria-label="عرض" 
                            size="small"
                            onClick={() => handleViewCoupon(coupon._id)}
                          >
                            <ViewIcon fontSize="small" />
                          </IconButton>
                          <IconButton 
                            aria-label="تعديل" 
                            size="small"
                            onClick={() => handleEditCoupon(coupon._id)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton 
                            aria-label="حذف" 
                            size="small"
                            onClick={() => handleOpenDeleteDialog(coupon._id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredCoupons.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="عدد الصفوف في الصفحة:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} من ${count}`}
          />
        </>
      )}

            {/* محتوى التبويب الثاني - الكوبونات المرسلة عبر SMS */}
      {tabValue === 1 && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" component="h2" dir="rtl">
              الكوبونات التلقائية
            </Typography>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<AutoAwesomeIcon />}
              onClick={handleOpenAutoCouponDialog}
            >
              إصدار كوبنات تلقائية
            </Button>
          </Box>


          {/* شريط البحث والفلترة */}
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              placeholder="ابحث عن كوبون أو رقم هاتف..."
              variant="outlined"
              size="small"
              value={sentCouponsSearchTerm}
              onChange={(e) => setSentCouponsSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ maxWidth: 500, mb: 2 }}
            />
            
            {/* فلترات إضافية */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              {/* فلتر نوع الكوبون */}
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>نوع الكوبون</InputLabel>
                <Select
                  value={couponTypeFilter}
                  label="نوع الكوبون"
                  onChange={(e) => setCouponTypeFilter(e.target.value)}
                >
                  <MenuItem value="all">الكل</MenuItem>
                  <MenuItem value="automatic">كوبون تلقائي</MenuItem>
                  <MenuItem value="automatic_sms">كوبون تلقائي SMS</MenuItem>
                  <MenuItem value="automatic_whatsapp">كوبون تلقائي واتس</MenuItem>
                </Select>
              </FormControl>

              {/* فلتر تاريخ البداية */}
              <TextField
                type="date"
                size="small"
                label="من تاريخ"
                value={startDateFilter}
                onChange={(e) => setStartDateFilter(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 180 }}
              />

              {/* فلتر تاريخ النهاية */}
              <TextField
                type="date"
                size="small"
                label="إلى تاريخ"
                value={endDateFilter}
                onChange={(e) => setEndDateFilter(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 180 }}
              />

              {/* زر مسح الفلاتر */}
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  setCouponTypeFilter('all');
                  setStartDateFilter('');
                  setEndDateFilter('');
                  setSentCouponsSearchTerm('');
                }}
              >
                مسح الفلاتر
              </Button>

              {/* زر حذف الكوبونات المحددة */}
              {selectedCoupons.length > 0 && (
                <Button
                  variant="contained"
                  color="error"
                  size="small"
                  startIcon={<DeleteIcon />}
                  onClick={() => setBulkDeleteDialogOpen(true)}
                >
                  حذف المحدد ({selectedCoupons.length})
                </Button>
              )}
            </Box>
          </Box>

          {sentCouponsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : sentCouponsError ? (
            <Box sx={{ py: 2 }}>
              <Typography color="error" align="center">{sentCouponsError}</Typography>
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table sx={{ minWidth: 650 }} aria-label="جدول الكوبونات المرسلة">
                 <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" align="center">
                        <Checkbox
                          checked={selectAll}
                          onChange={(e) => handleSelectAll(e.target.checked)}
                          indeterminate={selectedCoupons.length > 0 && selectedCoupons.length < filteredSentCoupons.length}
                        />
                      </TableCell>
                      <TableCell align="right">الكود</TableCell>
                      <TableCell align="right">نوع الكوبون</TableCell>
                      <TableCell align="right">رقم الهاتف</TableCell>
                      <TableCell align="right">قيمة الخصم</TableCell>
                      <TableCell align="right">الحد الأقصى للاستخدام</TableCell>
                      <TableCell align="right">عدد مرات الاستخدام</TableCell>
                      <TableCell align="right">تاريخ الإرسال</TableCell>
                      <TableCell align="right">الحالة</TableCell>
                      <TableCell align="center">الإجراءات</TableCell>
                    </TableRow>
                  </TableHead>
                 <TableBody>
                   {filteredSentCoupons.length === 0 ? (
                       <TableRow>
                         <TableCell colSpan={10} align="center">
                           {sentCouponsSearchTerm ? 'لا توجد كوبونات تطابق البحث' : 'لا توجد كوبونات مرسلة'}
                         </TableCell>
                       </TableRow>
                     ) : (
                      filteredSentCoupons.map((coupon) => (
                         <TableRow key={coupon._id} hover>
                            <TableCell padding="checkbox" align="center">
                              <Checkbox
                                checked={selectedCoupons.includes(coupon._id)}
                                onChange={(e) => handleSelectCoupon(coupon._id, e.target.checked)}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Chip
                                label={coupon.code}
                                color="primary"
                                size="small"
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Chip
                                label={getCouponTypeLabel(getCouponType(coupon.description))}
                                color="info"
                                size="small"
                              />
                            </TableCell>
                            <TableCell align="right">{coupon.phoneNumber}</TableCell>
                           <TableCell align="right">
                             {coupon.discountValue ? `${coupon.discountValue} ${coupon.discountType === 'percentage' ? '%' : 'ر.س'}` : '-'}
                           </TableCell>
                           <TableCell align="right">
                             {coupon.maxUsageCount ? coupon.maxUsageCount : 'غير محدود'}
                           </TableCell>
                           <TableCell align="right">
                             {coupon.usageCount !== undefined ? coupon.usageCount : 0}
                           </TableCell>
                           <TableCell align="right">
                             {new Date(coupon.sentAt).toLocaleDateString('ar-SA')}
                           </TableCell>
                           <TableCell align="right">
                             <Chip
                               label={coupon.status === 'sent' ? 'تم الإرسال' : coupon.status}
                               color={coupon.status === 'sent' ? 'success' : 'default'}
                               size="small"
                             />
                           </TableCell>
                           <TableCell align="center">
                             <IconButton 
                               aria-label="حذف" 
                               size="small"
                               onClick={() => handleOpenDeleteSentCouponDialog(coupon._id)}
                             >
                               <DeleteIcon fontSize="small" />
                             </IconButton>
                           </TableCell>
                         </TableRow>
                       ))
                   )}
                 </TableBody>
              </Table>
            </TableContainer>
          )}

           {!sentCouponsLoading && !sentCouponsError && (
             <TablePagination
               rowsPerPageOptions={[5, 10, 20, 25, 50, 100, 500]}
               component="div"
               count={totalSentCoupons}
               rowsPerPage={sentCouponsRowsPerPage}
               page={sentCouponsPage}
               onPageChange={handleSentCouponsChangePage}
               onRowsPerPageChange={handleSentCouponsChangeRowsPerPage}
               labelRowsPerPage="عدد الصفوف في الصفحة:"
               labelDisplayedRows={({ from, to, count }) => `${from}-${to} من ${count}`}
             />
           )}
        </>
      )}

       {/* حوار تأكيد حذف الكوبون المرسل */}
      <Dialog
        open={deleteSentCouponDialogOpen}
        onClose={handleCloseDeleteSentCouponDialog}
        aria-labelledby="alert-dialog-title-sent"
        aria-describedby="alert-dialog-description-sent"
      >
        <DialogTitle id="alert-dialog-title-sent" dir="rtl">
          تأكيد حذف الكوبون المرسل
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description-sent" dir="rtl">
            هل أنت متأكد من رغبتك في حذف هذا الكوبون المرسل؟ هذا الإجراء لا يمكن التراجع عنه.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteSentCouponDialog} color="primary">
            إلغاء
          </Button>
          <Button onClick={handleDeleteSentCoupon} color="error" autoFocus>
            حذف
          </Button>
        </DialogActions>
      </Dialog>

      {/* حوار تأكيد الحذف الجماعي */}
      <Dialog
        open={bulkDeleteDialogOpen}
        onClose={handleCloseBulkDeleteDialog}
        aria-labelledby="bulk-delete-dialog-title"
        aria-describedby="bulk-delete-dialog-description"
      >
        <DialogTitle id="bulk-delete-dialog-title" dir="rtl">
          تأكيد حذف الكوبونات المحددة
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="bulk-delete-dialog-description" dir="rtl">
            هل أنت متأكد من رغبتك في حذف {selectedCoupons.length} كوبون محددة؟ هذا الإجراء لا يمكن التراجع عنه.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBulkDeleteDialog} color="primary">
            إلغاء
          </Button>
          <Button onClick={handleBulkDeleteCoupons} color="error" autoFocus>
            حذف الكوبونات
          </Button>
        </DialogActions>
      </Dialog>

      {/* حوار تأكيد الحذف */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title" dir="rtl">
          تأكيد حذف الكوبون
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description" dir="rtl">
            هل أنت متأكد من رغبتك في حذف هذا الكوبون؟ هذا الإجراء لا يمكن التراجع عنه.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="primary">
            إلغاء
          </Button>
          <Button onClick={handleDeleteCoupon} color="error" autoFocus>
            حذف
          </Button>
                </DialogActions>
      </Dialog>

      {/* نافذة إصدار الكوبونات التلقائية */}
      <Dialog
        open={autoCouponDialogOpen}
        onClose={handleCloseAutoCouponDialog}
        aria-labelledby="auto-coupon-dialog-title"
        aria-describedby="auto-coupon-dialog-description"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="auto-coupon-dialog-title" dir="rtl">
          إصدار كوبنات تلقائية
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="auto-coupon-dialog-description" dir="rtl" sx={{ mb: 2 }}>
            اختر الكوبون الأصلي وحدد عدد الكوبونات التلقائية المراد إصدارها
          </DialogContentText>
          
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>الكوبون الأصلي</InputLabel>
            <Select
              value={selectedBaseCoupon}
              onChange={(e) => setSelectedBaseCoupon(e.target.value)}
              label="الكوبون الأصلي"
              disabled={loadingCoupons}
            >
              {availableCouponsForGeneration.map((coupon) => (
                <MenuItem key={coupon._id} value={coupon._id}>
                  {coupon.code} - {coupon.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="عدد الكوبونات التلقائية"
            type="number"
            value={autoCouponCount}
            onChange={(e) => setAutoCouponCount(parseInt(e.target.value) || 0)}
            inputProps={{ min: 1, max: 1000 }}
            sx={{ mb: 2 }}
          />

          {generatingCoupons && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2">جاري إصدار الكوبونات...</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAutoCouponDialog} color="primary" disabled={generatingCoupons}>
            إلغاء
          </Button>
          <Button 
            onClick={handleGenerateAutoCoupons} 
            color="primary" 
            variant="contained"
            disabled={generatingCoupons || !selectedBaseCoupon}
            startIcon={<AutoAwesomeIcon />}
          >
            إصدار الكوبونات
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar للإشعارات */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};


export default CouponList;